/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Storytelling
 * (C) Copyright IBM Corp. 2014, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'baglass/core-client/js/core-client/ui/core/View',
	'baglass/core-client/js/core-client/utils/BrowserUtils',
	'baglass/core-client/js/core-client/utils/ContentFormatter',
	'baglass/core-client/js/core-client/utils/dom-utils',
	'baglass/core-client/js/core-client/utils/Utils',
	'text!./templates/FilmStripView.html',
	'text!./templates/SceneCell.html',
	'text!./templates/AddSceneCell.html',
	'text!./templates/OverviewCell.html',
	'jquery',
	'underscore',
	'doT',
	'../nls/StringResources',
	'gemini/app/ui/dialogs/RenameDialog',
	'text!../layout/templates/sceneLayoutListing.json',
	'storytelling-ui/storytelling-ui.min',
	'./TimelineView',
	'gemini/app/ui/dnd/DnDHelper',
	'../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/chevron-down_16',
	'../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/add_16',
	'../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/maximize_16',
	'../lib/@ba-ui-toolkit/ba-graphics/dist/icons-js/overflow-menu--horizontal_16',
	'../lib/@ba-ui-toolkit/ba-graphics/dist/illustrations-js/visualization-widget_128',
	'react',
	'react-dom',
	'hammerjs',
	'jquery.hammer'
], function(View, BrowserUtils, ContentFormatter, DomUtils, Utils,
	FilmStripViewTemplate, SceneCellTemplate, AddSceneCellTemplate, OverviewCellTemplate,
	$, _, dot, stringResources, RenameDialog, sceneLayoutListing,
	StorytellingUI, TimelineView, DnDHelper, ChevronDownIcon, addIcon, maximizeIcon, menuOverflowIcon, visualizationIcon,
	React, ReactDOM) {

	var FilmStripView = View.extend({

		templateString: FilmStripViewTemplate,
		collapseDuration: 400,
		enlargeDuration: 400,

		events: {
			'keydown .scene, .overview, .addScene': 'onKeydown'
		},

		init: function(options) {
			FilmStripView.inherited('init', this, arguments);

			this.dashboardApi = options.dashboardApi;

			this.controller = options.controller; // StoryPaneController
			this.timelineController = this.controller.getTimelineController();
			this.canvasController = this.timelineController.canvasController;

			this.glassContext = options.glassContext;
			this.dndManager = options.dndManager;
			this.services = options.services;

			this._dropZones = [];

			this.logger = this.dashboardApi.getGlassCoreSvc('.Logger');
			this.translationService = this.dashboardApi.getDashboardCoreSvc('TranslationService');

			this.nextPreviousDuration = 900;
			this.nextPreviousDelay = this.nextPreviousDuration / 6;
			this.forceReFocus = false;

			this.flyoutHolder = document.createElement('div');
			this.flyoutHolder.classList.add('flyout-holder');
		},

		/**
		 * Renders the film strip
		 *
		 * @returns
		 */
		render: function() {
			this.$el.addClass('filmstrip');
			this.contentEl = this.el.parentNode; // FIXME: This line is bad. Filmstrip is technically reaching out of its scope

			this.controller.on('change:pageSize', this.onPageSizeChange, this);
			this.controller.on('change:showOverviews', this.onShowOverviewsChanged, this);
			this.controller.on('layoutType:changed', this.onLayoutTypeChanged, this);
			this.controller.on('mode:change', this.onModeChanged, this);
			this.controller.on('scene:add', this.onSceneAdded, this);
			this.controller.on('scene:collapse', this.onSceneCollapse, this);
			this.controller.on('scene:expand', this.onSceneExpand, this);
			this.controller.on('scene:remove', this.onSceneRemoved, this);
			this.controller.on('scene:select', this.onSceneSelected, this);

			this.controller.model.on('change:layout', this.onChangeLayout, this);

			this.timelineController.on('scene:reorder', this.onSceneReorder, this);

			this.canvasController.addRemoveNotifier.on('widget:addDone', this.onWidgetAdded, this);
			this.canvasController.addRemoveNotifier.on('widget:removeDone', this.onWidgetRemoved, this);

			var sHtml = this.dotTemplate({});
			this.$el.html(sHtml);
			this.$sceneSequencer = this.$el.find('.sceneSequencer');
			this.$sceneList = this.$el.find('.sceneList');

			this.$sceneSequencer.hammer()
				.on('mousedown', '.scene', this.onSceneContextMenu.bind(this))
				.on('clicktap', '.scene', this.onSceneClick.bind(this))
				.on('dragstart', '.scene', this.onSceneDragStart.bind(this)) // hammer
				.on('hold', '.scene', this.onSelectedSceneHold.bind(this)) // hammer
				.on('primaryaction', 'span.overflow', this.onSceneOverflowClick.bind(this))
				.on('primaryaction', 'span.expandScene', this.onExpandSceneClick.bind(this));

			this.cellTemplate = dot.template(SceneCellTemplate);

			this.controller.getScenes().forEach((item, index) => {
				const $cell = this.renderSceneCell(item, index);
				this.$sceneList.append($cell);
				this.middleShortenSceneTitle(this._getSceneSelector(item.id));
			});

			this.updateOverviewButton();
			this._setupFilmStripDropZone();
			this._toggleModeChanged(this.controller.isAuthoring());

			return Promise.all([
				this._addExpandSceneCoachmark(),
				this._addOverviewSceneCoachmark()]);
		},

		_scaleCell: function($cell, pageSize) {
			if(pageSize && pageSize.width && pageSize.height) {
				const DEFAULT_SCENE_HEIGHT = 125;
				$cell.css({
					width: DEFAULT_SCENE_HEIGHT * pageSize.width / pageSize.height + 'px',
					height: DEFAULT_SCENE_HEIGHT + 'px'
				});
			}
		},

		renderSceneCell: function(sceneModel, index) {
			const sHtml = this.cellTemplate({
				id: sceneModel.id,
				index: index,
				title: sceneModel.get('title'),
				expandSceneLabel: stringResources.get('storySceneExpand'),
				sceneActionMenuLabel: stringResources.get('storySceneActionMenu'),
				selected: index === this.controller.getSelectedSceneIndex(),
				widgetIndicated: this.controller.isScenePopulated(sceneModel.id)
			});
			const $cell = $(sHtml);

			this.translationService.registerView({ view: $cell, model: sceneModel });

			Utils.setIcon($cell.find('.expandScene'), maximizeIcon.default.id);
			Utils.setIcon($cell.find('.overflow'), menuOverflowIcon.default.id);
			Utils.setIcon($cell.find('.widgetIndicator'), visualizationIcon.default.id);

			sceneModel.on('change:title', () => {
				this.onSceneLabelChange(sceneModel);
			});

			this._scaleCell($cell, this.controller.model.layout.pageSize);
			this._setupDropZones($cell);

			return $cell;
		},

		_setupDropZones: function($cell) {
			const cell = $cell[0];
			const dropZone = this.dndManager.addDropTarget(cell, {
				accepts: function(dragObject) {
					return dragObject.type === 'sceneCell' || dragObject.type === 'widget';
				},
				onDragEnter: (dragObject, dropNode) => {
					if(dragObject.type === 'widget') {
						dropNode.classList.add('dropZone');
					}
				},
				onDragMove: (dragObject, dropNode) => {
					if(dragObject.type !== 'sceneCell') {
						return;
					}

					this._dropNodeModelId = dropNode.dataset.modelId;
					const dropModelIndex = this.controller.getSceneIndex(this._dropNodeModelId);

					if(this._dragModelIndex > dropModelIndex) {
						this.$sceneList[0].insertBefore(this._cloneDragEl, dropNode);
					} else {
						this.$sceneList[0].insertBefore(this._cloneDragEl, dropNode.nextSibling);
					}
				},
				onDrop: (dragObject) => {
					if(dragObject.type === 'widget') {
						DnDHelper.handleWidgetDrop(this.dashboardApi.getCanvas(), $cell.attr('data-model-id'), dragObject);
						$cell[0].classList.remove('dropZone');
					}
				},
				onDragLeave: (dragObject, dropNode) => {
					if(dragObject.type === 'widget') {
						dropNode.classList.remove('dropZone');
					}
				}
			});

			this._dropZones.push({ dropZone, node: cell });
		},

		/**
		 * A method to setup a dropzone on the entire filmstrip to handle drag-and-drop events
		 * on scenes that are dropped outside the sceneList container.
		 */
		_setupFilmStripDropZone: function() {
			let filmstrip = this.el;
			let sceneList = this.$sceneList[0];

			const dropZone = this.dndManager.addDropTarget(filmstrip, {
				accepts: dragObject => (dragObject.type === 'sceneCell'),

				onDragStart: () => {
					this.sceneListBounds = sceneList.getBoundingClientRect();
				},

				onDragMove: dragObject => {
					const firstScene = sceneList.firstChild;
					const lastScene = sceneList.lastChild;

					if (dragObject.position.x < this.sceneListBounds.left) {
						if (firstScene !== this._cloneDragEl) {
							this._dropNodeModelId = firstScene.dataset.modelId;
							sceneList.insertBefore(this._cloneDragEl, firstScene);
						}
					} else if (dragObject.position.x > this.sceneListBounds.left + this.sceneListBounds.width) {
						if (lastScene !== this._cloneDragEl) {
							this._dropNodeModelId = lastScene.dataset.modelId;
							sceneList.appendChild(this._cloneDragEl);
						}
					}
				}
			});

			this._dropZones.push({ dropZone, node: filmstrip });
		},

		/**
		 * Create the "add scene" cell that lives at the end of the filmstrip
		 * but before the end overview scene if it exists and is turned on.
		 */
		renderAddSceneCell: function() {
			const addCellTemplate = dot.template(AddSceneCellTemplate);
			const sHtml = addCellTemplate({
				addTitle: stringResources.get('addSceneBtnTitle'),
				chevronTitle: stringResources.get('addSceneChevronBtnTitle')
			});

			this.$addSceneCell = $(sHtml);
			// Place the 'add scene' cell after the 'sceneSequencer' container
			this.$sceneSequencer.after(this.$addSceneCell);
			this._scaleCell(this.$addSceneCell, this.controller.model.layout.pageSize);

			let $plusContainer = this.$addSceneCell.find('.addContainer');
			Utils.setIcon($plusContainer, addIcon.default.id);
			$plusContainer.on('click', this.onAddSceneClick.bind(this));

			let $chevronContainer = this.$addSceneCell.find('.chevron');
			Utils.setIcon($chevronContainer, ChevronDownIcon.default.id);
			$chevronContainer.on('primaryaction', this.onAddSceneOverflowClick.bind(this));
		},

		onSceneLabelChange: function(model) {
			const sceneSelector = this._getSceneSelector(model.id);
			this._getTitleEl(sceneSelector).textContent = model.get('title') || '';

			this.middleShortenSceneTitle(sceneSelector);
		},

		middleShortenSceneTitle: function(sceneSelector) {
			const sceneTitleEl = this._getTitleEl(sceneSelector);
			ContentFormatter.middleShortenString(sceneTitleEl);
		},

		_getTitleEl: function(sceneSelector) {
			return this.el.querySelector(sceneSelector + ' .pageTitle');
		},

		onShowOverviewsChanged: function() {
			this.updateOverviewButton();
		},

		updateOverviewButton: function(updateIcon) {
			if(this.controller.showAuthoringOverview()) {
				if(!this._$overviewStart) {
					this._$overviewStart = this._renderOverviewButton(-1);
					this.$el.prepend(this._$overviewStart);
					this._scaleCell(this._$overviewStart, this.controller.model.layout.pageSize);
				} else if(updateIcon) {
					this._updateOverviewIcon(this.$el.find('.overview.start .layoutIcon'), -1);
				}
				// This bit is for toggling the scene number in the overview frame to show if it is shown or not.
				var $pageNum = this._$overviewStart.find('.sceneSubContainer .pageInfo');
				if(this.controller.showStartOverview()) {
					$pageNum.show();
				} else {
					$pageNum.hide();
				}
			} else {
				if(this._$overviewStart) {
					this._$overviewStart.remove();
					this._$overviewStart = null;
				}
			}

			if(this.controller.showEndOverview()) {
				if(!this._$overviewEnd) {
					this._$overviewEnd = this._renderOverviewButton(-2);
					this.$el.append(this._$overviewEnd);
					this._scaleCell(this._$overviewEnd, this.controller.model.layout.pageSize);
				} else if(updateIcon) {
					this._updateOverviewIcon(this.$el.find('.overview.end .layoutIcon'), -2);
				}
			} else {
				if(this._$overviewEnd) {
					this._$overviewEnd.remove();
					this._$overviewEnd = null;
				}
			}
		},

		/**
		 * Render an overview scene cell
		 * @param {Number} index The index value that relates to which overview scene to handle rendering of
		 */
		_renderOverviewButton: function(index) {
			// if index is -1, this is the beginning overview button (overviewId should be 0)
			// if index is -2, this is the end overview button (overviewId should be # scenes + 1)
			const label = this._getOverviewLabel(index);

			if(!this.overviewCellTemplate) {
				this.overviewCellTemplate = dot.template(OverviewCellTemplate);
			}

			const sHtml = this.overviewCellTemplate({
				title: label,
				selected: index === this.controller.getSelectedSceneIndex(),
				index: this._getOverviewIndex(index)
			});
			const $el = $(sHtml);

			if(index === -1) {
				$el.addClass('start')
					.on('primaryaction', this.onOverviewStartClick.bind(this));
			} else if (index === -2) {
				$el.addClass('end')
					.on('primaryaction', this.onOverviewEndClick.bind(this));
			}
			this._updateOverviewIcon($el.find('.layoutIcon'), index);

			return $el;
		},

		_getOverviewLabel: function(index) {
			let label;

			if (index === -1) {
				label = stringResources.get('sceneListStart');
			} else if(index === -2) {
				label = stringResources.get('sceneListEnd');
			} else {
				label = stringResources.get('overviewBtnLabel');
			}

			return label;
		},

		_getOverviewIndex: function(index) {
			const $scenes = this.$sceneList.children();
			let overviewIndex = null;
			if(index === -1) {
				overviewIndex = 0;
			} else if(index === -2) {
				overviewIndex = $scenes.length + 1;
			}
			return overviewIndex;
		},

		_updateOverviewIcon: function($layoutIcon, index) {
			var navModel = this.controller.getNavModel();
			var iconName = navModel.indexOf('panAndZoom') >= 0 ? 'dashboard-' + navModel : null;
			var label = this._getOverviewLabel(index);
			if(iconName) {
				$layoutIcon.empty();
				Utils.setIcon($layoutIcon, iconName, label);
			}
		},

		remove: function() {
			if(this.controller){
				this.controller.off('change:pageSize', this.onPageSizeChange, this);
				this.controller.off('change:showOverviews', this.onShowOverviewsChanged, this);
				this.controller.off('layoutType:changed', this.onLayoutTypeChanged, this);
				this.controller.off('mode:change', this.onModeChanged, this);
				this.controller.off('scene:add', this.onSceneAdded, this);
				this.controller.off('scene:collapse', this.onSceneCollapse, this);
				this.controller.off('scene:expand', this.onSceneExpand, this);
				this.controller.off('scene:remove', this.onSceneRemoved, this);
				this.controller.off('scene:select', this.onSceneSelected, this);

				const scenes = this.controller.getScenes() || [];
				scenes.forEach(scene => {
					scene.off('change:title', null, this);
				});
				if(this.controller.model){
					this.controller.model.off('change:layout', this.onChangeLayout, this);
				}
			}


			this.timelineController.off('scene:reorder', this.onSceneReorder, this);

			// This check is here because there is a destruction path that causes the
			// addRemoveNotifier to be unset before execution gets here
			if(this.canvasController && this.canvasController.addRemoveNotifier) {
				this.canvasController.addRemoveNotifier.off('widget:addDone', this.onWidgetAdded, this);
				this.canvasController.addRemoveNotifier.off('widget:removeDone', this.onWidgetRemoved, this);
			}

			if (this._dropZones) {
				this._dropZones.forEach(zone => {
					if (zone.dropZone && zone.node) {
						zone.dropZone.remove();
						// Remove scenes from the filmstrip
						if (zone.node !== this.el) {
							zone.node.parentNode.removeChild(zone.node);
						}
					}
				});

				this._dropZones = [];
			}

			FilmStripView.inherited('remove', this, arguments);
		},

		onAddSceneClick: function() {
			this.controller.addScene().catch((error) => {
				this.logger.error('Unable to add new scene to story', error);
			});
		},

		onSceneClick: function(event) {
			event.stopPropagation();
			this._closeFlyout();

			const index = this.$sceneList.children().index(event.currentTarget);
			const modelId = event.currentTarget.dataset.modelId;
			this.controller.selectScene({
				index,
				modelId
			});
		},

		onOverviewStartClick: function(event) {
			event.stopPropagation();
			this.controller.selectScene({ index: -1 });
		},

		onOverviewEndClick: function(event) {
			event.stopPropagation();
			this.controller.selectScene({ index: -2 });
		},

		onExpandSceneClick: function(event) {
			event.stopPropagation();
			this.controller.expandScene();
		},

		/**
		 * Controller events
		 */

		onModeChanged: function(event) {
			this._toggleModeChanged(event.authoring);
			this.updateOverviewButton();
		},

		/**
		 * @param {LayoutEvent} event This is the event triggered by StoryPaneController.onAddLayout()
		 */
		onSceneAdded: function(event) {
			this._addScene(event.scene, event.index, event.insertBefore);
			this._refreshCellPageNumbers();
		},

		onSceneRemoved: function(event) {
			this._removeScene(event.id);
			this._refreshCellPageNumbers();
			this.updateOverviewButton();
		},

		onSceneSelected: function(event) {
			this._selectScene(event);
			this._refreshTimeline();
		},

		onSceneExpand: function(event) {
			this._expandScene(this.$sceneList.children().eq(event.index));
		},

		onSceneCollapse: function(event) {
			this._collapseScene(this.$sceneList.children().eq(event.index));
		},

		onLayoutTypeChanged: function() {
			this.updateOverviewButton(true);
		},

		onPageSizeChange: function(pageSize) {
			this.$sceneList.children().each((index, cell) => {
				this._scaleCell($(cell), pageSize);
			});

			this._scaleCell(this.$addSceneCell, pageSize);

			if(this._$overviewStart) {
				this._scaleCell(this._$overviewStart, pageSize);
			}

			if(this._$overviewEnd) {
				this._scaleCell(this._$overviewEnd, pageSize);
			}
		},

		onSceneContextMenu: function(event) {
			// right-click
			if(event.which === 3) {
				this.onSceneOverflowClick(event);
			}
		},

		onSceneOverflowClick: function(event) {
			event.stopPropagation();
			if(this.preventContext || !this.controller.isAuthoring()) {
				this.preventContext = false;
			} else {
				if(this._flyout) {
					this._closeFlyout();
					return;
				}

				let $target = $(event.currentTarget).closest('[data-model-id]');
				let sceneId = $target.data('modelId');

				var options = {
					name: this.controller.getScene(this.controller.getSceneIndex(sceneId)).get('title'),
					type: 'Scene',
					allowEmpty: true,
					id: _.uniqueId('renameDialog')
				};
				var renameDialog = new RenameDialog(options, function(newName) {
					this.controller.renameScene(sceneId, newName);
				}.bind(this));

				const renameDialogCallback = function() {
					renameDialog.open();
				};

				// Making sure the anchor element is always the scene
				let anchorElement = event.currentTarget;
				if (anchorElement.parentNode.id === sceneId + '_tablabel') {
					anchorElement = anchorElement.parentNode;
				}

				this.$el.append(this.flyoutHolder);
				this._flyout = <StorytellingUI.SceneCellFlyout
					sceneId={ sceneId }
					controller={ this.controller }
					anchorElement={ anchorElement }
					closeFlyout={ this._closeFlyout.bind(this) }
					renameDialog={ renameDialogCallback }
				/>;
				ReactDOM.render(this._flyout, this.flyoutHolder);
			}
		},

		onAddSceneOverflowClick: function(event) {
			//TODO: Find a better way to access this element
			let target = event.currentTarget.children[0];
			let options = {
				target: event.currentTarget,
				onOpen: function() {
					// can't use .classList.add() because it is not supported in IE11 for SVG elements
					target.setAttribute('class', 'svgIcon selected');
				},
				onClose: function() {
					// can't use .classList.remove() because it is not supported in IE11 for SVG elements
					target.setAttribute('class', 'svgIcon');
					this._closeFlyout();
				}.bind(this)
			};

			// Toggle open/close of add scene flyout
			if(this._flyout) {
				options.onClose();
			} else {
				this._showContextMenu(options);
			}
		},

		_showContextMenu: function(options) {
			if(this.preventContext || !this.controller.isAuthoring()) {
				this.preventContext = false;
			} else if(this.controller.isAuthoring()) {
				this.$el.append(this.flyoutHolder);
				this._flyout = (
					<StorytellingUI.AddSceneFlyout
						anchorElement={ options.target }
						closeFlyout={ options.onClose }
						onOpen={ options.onOpen }
						onLayoutSelection={ this.controller.addScene.bind(this.controller) }
						stringResources={ this.services.getSvcSync('.StringResources') }
						sceneLayoutListing={ JSON.parse(sceneLayoutListing).sceneLayouts }
					/>
				);
				ReactDOM.render(this._flyout, this.flyoutHolder);
			}
		},

		_closeFlyout: function() {
			if(this._flyout) {
				ReactDOM.unmountComponentAtNode(this.flyoutHolder);
				this.$el.find('.flyout-holder').remove();
				this._flyout = null;
				this.preventContext = false;
			}
		},

		/**
		 * Event handler for 'scene:reorder' event from TimelineController
		 */
		onSceneReorder: function(event) {
			const swapElements = function(element, target) {
				if(element !== target) {
					let parent = element.parentNode;
					let placeholder = parent.insertBefore(document.createTextNode(''), element);
					parent.insertBefore(element, target);
					parent.insertBefore(target, placeholder);
					parent.removeChild(placeholder);
				}
			};
			// Get the updated list of scenes (after they've been reordered)
			// and update the FilmStrip's scene list
			let updatedSceneList = event.model.getParent().items;
			_.each(updatedSceneList, function(scene, index) {
				let $currentCell = this.$sceneList.children().eq(index);
				let $cell = this.$sceneList.find(this._getSceneSelector(scene.id));
				swapElements($cell[0], $currentCell[0]);
			}, this);


			// Remove any coach marks from the scene when it is moved
			var $scene = this.$sceneList.find('#' + event.model.id + '_tablabel>.expandScene');
			$scene.find('.coachMark').remove();

			this._refreshCellPageNumbers();
		},

		/**
		 * The method used when listening for the 'dragstart' (hammer) event
		 * @param {Event} event A mouse event
		 */
		onSceneDragStart: function(event) {
			if(!DomUtils.isPointerTouch(event) && this.controller.isAuthoring()) {
				this._startReorderScenes(event);
			}
		},

		/**
		 * The method used when listening for the 'hold' (hammer) event
		 * @param {Event} event A mouse event
		 */
		onSelectedSceneHold: function(event) {
			if (event.currentTarget.classList.contains('selected') && this.controller.isAuthoring()) {
				this._shakeOnHold(event.currentTarget);
				this._startReorderScenes(event);
			}
		},

		/**
		 * @private
		 * @param {Event} event The event generated from a mouse drag or touch hold
		 */
		_startReorderScenes: function(event) {
			let sceneEl = event.currentTarget;
			let $sceneEl = $(sceneEl);
			let $avatar = this._makeAvatar($sceneEl);

			this._cloneDragEl = $avatar[0].cloneNode();
			this._cloneDragEl.classList.remove('sceneMoveAvatar');
			this._cloneDragEl.classList.add('scenePlaceholder');
			this._dragModelIndex = this.controller.getSceneIndex(event.currentTarget.dataset.modelId);

			const sceneModel = this.controller.getSceneById(event.currentTarget.dataset.modelId);
			const targetSelected = sceneEl.classList.contains('selected');

			/* Make sure the avatar is aligned with the labels*/
			let offset = $sceneEl.offset();
			let eventPos = DomUtils.getEventPos(event);
			let avatarYOffset = offset.top - eventPos.pageY;
			let avatarXOffset = offset.left - eventPos.pageX;

			this.dndManager.startDrag({
				event: event,
				type: 'sceneCell',
				data: sceneModel,
				avatar: $avatar[0],
				moveXThreshold: 20, // move horizontally 20 pixels to enable dragging. This will allow vertical scrolling/panning
				avatarYOffset: avatarYOffset,
				avatarXOffset: avatarXOffset,
				restrictToXAxis: true,
				callerCallbacks: {
					onDragStart: () => {
						this.$el.addClass('noScroll');
						this._dropNodeModelId = null;

						this.$sceneList[0].insertBefore(this._cloneDragEl, sceneEl);

						sceneEl.style.display = 'none';
						sceneEl.classList.remove('selected');
						sceneEl.classList.remove('shake');
					},
					onDragDone: () => {
						if(this._dropNodeModelId) {
							const dropModelIndex = this.controller.getSceneIndex(this._dropNodeModelId);
							const dropModel = this.controller.getSceneById(this._dropNodeModelId);

							this.beforeModelId = dropModel.getNextSiblingId();
							if(this._dragModelIndex > dropModelIndex) {
								this.beforeModelId = dropModel.id;
							}
						}

						this.$sceneList[0].insertBefore(sceneEl, this._cloneDragEl);

						if(BrowserUtils.isIE11()) {
							this.$sceneList[0].removeChild(this._cloneDragEl);
						} else {
							this._cloneDragEl.remove();
						}

						sceneEl.style.display = '';
						if(targetSelected) {
							sceneEl.classList.add('selected');
						}
						this.el.classList.remove('noScroll');

						if(this._dropNodeModelId) {
							this.controller.moveViewBefore(sceneModel, this.beforeModelId);
						}

						this.beforeModelId = null;
						this._cloneDragEl = null;
						this._dropNodeModelId = null;
						return true;
					}
				}
			});
		},

		/**
		 * Clone an element and append it to the body of the document
		 * @private
		 * @param {jQuery} $target A jQuery object representing an element on the page that will be cloned
		 *
		 * @return {jQuery} The cloned element as a jquery object
		 */
		_makeAvatar: function($target) {
			let $avatar = $target.clone().addClass('sceneMoveAvatar');
			let avatar = $avatar[0];

			// Remove some unneeded attributes (from the clone) before placing the element on the page
			avatar.removeAttribute('id');
			avatar.removeAttribute('tabindex');
			avatar.removeAttribute('role');
			avatar.removeAttribute('data-model-id');

			$avatar.find('.overflow').remove();
			$avatar.find('.expandScene').remove();
			$avatar.find('.pageInfo').remove();

			document.body.appendChild(avatar);

			return $avatar;
		},

		onWidgetAdded: function(event) {
			var sceneId = this._getWidgetSceneId(event.id);
			this._widgetIndicated(sceneId);

		},

		onWidgetRemoved: function(event) {
			var sceneId = this._getWidgetSceneId(event.id);
			this._widgetIndicated(sceneId);
		},

		/*
		* Helpers
		*/

		_getWidgetSceneId: function(widgetId) {
			let result;
			if(this.controller && this.controller.model){
				let parent = this.controller.model.layout.findTopLevelParentItem(widgetId);
				if (parent) {
					result = (this.controller.getSceneIndex(parent.id) > -1) ? parent.id : null;
				}
			}
			return result;
		},

		_widgetIndicated: function(sceneId) {
			var toggle;
			if(sceneId) {
				toggle = this.controller.isScenePopulated(sceneId);
				this.$el.find(this._getSceneSelector(sceneId) + ' .widgetIndicator').toggleClass('widgetIndicated', toggle);
			} else {
				toggle = this.timelineController.getTimelineEpisodeCount() > 0;
				this.$el.find('.scene.selected .widgetIndicator').toggleClass('widgetIndicated', toggle);
			}
		},

		_widgetIndicatedMove: function(moveSceneId, $cell) {
			//update widget indicator for selected scene
			this._widgetIndicated();
			//update widget indicator for scene widget is moved to
			var toggle = this.controller.isScenePopulated(moveSceneId);
			$cell.find('.widgetIndicator').toggleClass('widgetIndicated', toggle);
		},

		_findTopLevelParentItem: function(value) {
			var parents = [];
			if(value && value.parameter && value.parameter.updateArray) {
				var array = value.parameter.updateArray;
				parents = _.map(array, function(element) {
					var parent = this.controller.model.layout.findTopLevelParentItem(element.parentId);
					return (parent && parent.id && !this.controller.isOverview()) ? parent.id : element.parentId;
				}.bind(this));
			}

			return parents;
		},

		onChangeLayout: function(event) {
			this._updateWidgetIndicator(event);
			if(this.timelineView) {
				this._showTimeline(0);
			}
		},

		_updateWidgetIndicator: function(event) {
			var prevParentId = this._findTopLevelParentItem(event.prevValue);
			var newParentId = this._findTopLevelParentItem(event.value);

			if(prevParentId.length === 0 || newParentId.length === 0 || _.intersection(prevParentId, newParentId).length !== 0) {
				return;
			}

			for(var i = 0; i < prevParentId.length; i++) {
				this._widgetIndicated(prevParentId[i]);
			}
			for(i = 0; i < newParentId.length; i++) {
				this._widgetIndicated(newParentId[i]);
			}
		},

		_toggleModeChanged: function(authoringFlag) {
			this.$el.toggleClass('authoring', authoringFlag);

			const $addSceneEl = this.$el.find('.addScene');
			if(authoringFlag) {
				if($addSceneEl.length === 0) {
					this.renderAddSceneCell();
				}
			} else {
				// Otherwise remove the add scene cell and its events
				$addSceneEl.off().remove();
			}

			// Close timeline if user is leaving authoring mode
			if(!authoringFlag && this.timelineView) {
				this.controller.collapseScene();
			}
		},

		_getSceneSelector: function(id) {
			return '#' + id + '_tablabel';
		},

		/**
		 * Add a scene cell to the appropriate place in the sceneList
		 * @private
		 */
		_addScene: function(scene, index, insertBeforeId) {
			const $cell = this.renderSceneCell(scene, index);

			if(insertBeforeId) {
				this.$sceneList.find(this._getSceneSelector(insertBeforeId)).before($cell);
			} else {
				this.$sceneList.find('.scene:last-child').after($cell);
			}

			this._addExpandSceneCoachmark();
		},

		_removeScene: function(id) {
			// When a scene is removed from the film strip, we lose focus of the film strip.
			// selectScene() only focuses on a scene if the film strip is focused, so we force refocus.
			this.forceReFocus = true;
			const sceneSelector = this._getSceneSelector(id);
			const sceneCell = this.$sceneList[0].querySelector(sceneSelector);

			this._dropZones = this._dropZones.filter(zone => {
				if (zone.node === sceneCell) {
					zone.dropZone.remove();
					zone.node.parentNode.removeChild(zone.node);
					return false;
				}

				return true;
			});

			this.translationService.deregisterView(id);
		},

		_refreshCellPageNumbers: function() {
			const $scenes = this.$sceneList.children();
			for(var i = 0; i < $scenes.length; i++) {
				$scenes.eq(i).find('.pageNumber')[0].setAttribute('data-index', i + 1);
			}

			// if beginning and end overviews exist, reset their id
			// this will change once we allow enabling/disabling each overview
			const $overviews = this.$el.find('.overview');
			if($overviews.length >= 2) {
				$overviews.eq(0).find('.pageNumber')[0].setAttribute('data-index', 0);
				$overviews.eq(1).find('.pageNumber')[0].setAttribute('data-index', $scenes.length + 1);
			}
		},

		/**
		 * Add an empty div with 100% width to force the scroll view to be scrollable.
		 *
		 * Reason: scrolling of the scroll view is necessary during the animation to make it look slick.
		 * When the scroll view's content is not scrollable (ie. 1 or 2 scenes), then the scroll animation
		 * can only occur once the resizing cell is large enough to introduce scroll in the parent. This
		 * results in a jarring animation.
		 */
		_createAnimationDummyDiv: function() {
			return $('<div />').css({
				width: '100%',
				height: '1px',
				display: 'inline-block'
			});
		},

		_expandScene: function($scene) {
			const scrollLeft = this.$el.scrollLeft();

			const position = $scene.position();
			const marginLeft = parseInt($scene.css('margin-left'), 10);

			const originalWidth = $scene.css('width');
			const originalHeight = $scene.css('height');
			const $sceneElements = $scene.find('.sceneOverlay, .widgetIndicator');

			const duration = this.enlargeDuration;

			// Add an empty div with 100% width to force the scroll view to be scrollable.
			const dummyDiv = this._createAnimationDummyDiv().appendTo(this.$el);

			// remove coachmark
			this._removeExpandSceneCoachmark();

			// close flyout
			this._closeFlyout();

			this.$el
				// Issue on Firefox: scroll location changes when appending items. Reset the scroll.
				.scrollLeft(scrollLeft)
				// Prevent scrollbars from showing during the animation.
				.css('overflow-x', 'hidden');

			// The animation is about to begin.
			$scene.addClass('animating');

			// Fade out the overlay elements and the widgetIndicator element
			$sceneElements.fadeOut(duration / 3);

			// Animate the cell to fit the full width and height.
			$scene.animate({
				width: this.$el.outerWidth(true),
				height: this.$el.outerHeight(true),
				opacity: 0
			}, {
				duration,
				complete: () => {
					this._showTimeline(duration / 4, () => {
						// Hide the film strip view as we are now showing the timeline view.
						this.$el.hide();

						// The animation is finished.
						$scene.removeClass('animating');

						// Reset the cell size.
						$scene.css({
							width: originalWidth,
							height: originalHeight,
							opacity: 1
						});

						// Reset the display overlays and widgetIndicator
						$sceneElements.css('display', '');

						// Remove the dummy div.
						dummyDiv.remove();
					});
				}
			});

			// Animate to the end scroll position.
			this.$el.animate({
				scrollLeft: position.left + marginLeft + scrollLeft
			}, duration);

		},

		_collapseScene: function($scene) {
			this.$el.show();

			var scrollLeft = this.$el.scrollLeft();
			var fullWidth = this.$el.outerWidth(true);
			var fullHeight = this.$el.outerHeight(true);

			var marginLeft = parseInt($scene.css('margin-left'), 10);
			var originalWidth = $scene.css('width');
			var originalHeight = $scene.css('height');

			var duration = this.collapseDuration;

			// Add an empty div with 100% width to force the view to be scrollable.
			var $dummyDiv = this._createAnimationDummyDiv().appendTo(this.$sceneList);

			// Issue on Firefox: scroll location changes when appending items. Reset the scroll.
			this.$el.scrollLeft(scrollLeft);

			// The animation is about to begin.
			$scene.addClass('animating');

			// Fade-out and remove the timeline.
			if(this.timelineView) {
				this.timelineView.fadeOut({
					complete: () => {
						this.timelineView.remove();
						this.timelineView = null;
					},
					duration: duration / 4
				});
			}

			// Enlarge the overlays and widgetIndicator after the cell animation completes.
			$scene
				.find('.sceneOverlay, .widgetIndicator')
				.css({
					transform: 'scale(0)',
					opacity: 0
				})
				.delay(duration)
				.animate({
					transform: 1,
					opacity: 1
				}, {
					step: function(now, fx) {
						if(fx.prop === 'transform') {
							this.style.transform = 'scale('+ now +')';
						}
					},
					complete: function() {
						this.style.transform = null;
						this.style.opacity = null;
					},
					duration: duration / 2
				});

			// Prior to animation: set the resize cell to the full width and height.
			$scene.css({
				width: fullWidth,
				height: fullHeight,
				opacity: 0
			});

			// Prior to animation: set the scroll so that cell is aligned in the page.
			scrollLeft = this.$el.scrollLeft();
			this.$el.scrollLeft($scene.position().left + marginLeft + scrollLeft);

			// Animate back to the original cell size.
			$scene.animate({
				width: originalWidth,
				height: originalHeight,
				opacity: 1
			}, {
				duration: duration,
				complete: () => {
					// The animation is finished.
					$scene.removeClass('animating');

					// Reset scrollbars.
					this.$el.css('overflow-x', 'auto');

					// Remove the dummy div.
					$dummyDiv.remove();
				}
			});

			// Animate to the end scroll position.
			this.$el.animate({
				scrollLeft: scrollLeft - parseInt(this.$sceneList.css('margin-left'), 10)
			}, duration);
		},

		_refreshTimeline: function() {
			if(this.timelineView) {
				this.timelineView.fadeOut({
					complete: () => {
						// Guard against concurrent dismissal of the timeline, for instance.
						if(this.timelineView) {
							this._showTimeline(this.enlargeDuration / 4);
						}
					}
				});
			}
		},

		_showTimeline: function(duration, complete) {
			if(this.timelineView) {
				this.timelineView.remove();
				this.timelineView = null;
			}

			if(this.timelineController.getCurrentScene() !== null) {
				this._createTimeline();
				this.timelineView.renderFadeIn({
					complete,
					duration
				});
			} else {
				// If execution gets in here it means one of two things:
				// 1. User navigated to an overview (in GJ)
				// 2. The bug in IE when converting caused current scene data to be lost
				let sceneIndex = this.controller.currentSceneIndex;
				if(sceneIndex === -1) {
					sceneIndex = 0;
				} else if(sceneIndex === -2) {
					sceneIndex = this.controller.getSceneCount()-1;
				}
				this._collapseScene(this.$sceneList.children().eq(sceneIndex));
			}
		},

		_createTimeline: function() {
			const timelineNode = document.createElement('div');
			this.contentEl.appendChild(timelineNode); // FIXME: This line is bad. Filmstrip is technically reaching out of its scope

			this.timelineView = new TimelineView({
				el: timelineNode,
				storyController: this.controller,
				controller: this.timelineController,

				dashboardApi: this.dashboardApi,
				dndManager: this.dndManager,

				glassContext: this.glassContext,
				services: this.services
			});
		},

		_removeExpandSceneCoachmark: function() {
			// following line is document scoped because we may need to remove in many opened stories.
			// After some testing it seems like glass now removes an inactive perspective's HTML from the DOM
			// so this document scoped query isn't needed anymore as it doesn't work as desired
			var $firstScenes = $('.expandScene.coachMarkContainer');
			if($firstScenes.find('.coachMark:visible').length) {
				var coachMarkReadId = $firstScenes.attr('id');
				var persistence = this.dashboardApi.getGlassCoreSvc('.CoachMarkService').getPersistence();
				persistence.marksAsRead(coachMarkReadId);
				$firstScenes.find('.coachMark').hide();
			}
		},

		_addExpandSceneCoachmark: function() {
			const $target = this.$sceneList.find('.expandScene').first();
			const options = {
				id: 'com.ibm.bi.dashboard.filmstrip.expandScene',
				domElement: $target[0],
				title: stringResources.get('expandSceneCoachmarkTitle'),
				contents: stringResources.get('expandSceneCoachmarkContents')
			};
			this.dashboardApi.prepareGlassOptions(options);

			const coachMarkApi = this.dashboardApi.getFeature('CoachMark');
			return coachMarkApi.addCoachMark(options);
		},

		_addOverviewSceneCoachmark: function() {
			const $target = this.$el.find('.overview').first();
			if($target.length === 1) {
				const options = {
					id: 'com.ibm.bi.dashboard.filmstrip.overview',
					domElement: $target[0],
					title: stringResources.get('overviewCoachmarkTitle'),
					contents: stringResources.get('overviewCoachmarkContents')
				};
				this.dashboardApi.prepareGlassOptions(options);

				const coachMarkApi = this.dashboardApi.getFeature('CoachMark');
				return coachMarkApi.addCoachMark(options);
			}

			return Promise.resolve();
		},

		_selectScene: function(event) {
			this._closeFlyout();

			const sceneId = event.scene && event.scene.id;
			if(!sceneId) {
				return;
			}

			let $scene;
			if(sceneId === 'start' || sceneId === 'end') {
				$scene = this.$el.find('.overview.'+ sceneId);
			} else {
				$scene = this.$sceneList.find(this._getSceneSelector(sceneId));
			}

			if($scene.length !== 1) {
				return;
			}

			this.$el.find('.overview.selected, .scene.selected')
				.removeClass('selected')
				.attr('tabindex', -1);

			$scene.addClass('selected').attr('tabindex', 0);
			if(this.forceReFocus || $.contains(this.el, document.activeElement) && $scene.is(':visible')) {
				// If we are focused inside the film strip or if the force refocus flag is set, move the focus.
				$scene.focus();
				this.forceReFocus = false;
			}

			const scroller = this._getSceneScrollPosition($scene);
			this.$el
				.scrollLeft(scroller.scrollLeft)
				.animate({
					scrollLeft: scroller.position
				}, 400);
		},

		/**
		 * @param {Element} target The DOM element to add the shake class to
		 */
		_shakeOnHold: function(target) {
			this._closeFlyout();
			this.preventContext = true;
			target.classList.add('shake');
			this.el.classList.add('noScroll');

			// note: we use "one" here to make sure it only runs once (for each event type)
			$(target).one('mouseup touchup', function() {
				target.classList.remove('shake');
				this.el.classList.remove('noScroll');
				this.preventContext = false;
			}.bind(this));
		},

		_wrapEvent: function(event) {
			return {
				code: event.keyCode || event.charCode,
				modifier: event.ctrlKey || event.metaKey,
				altKey: event.altKey,
				shiftKey: event.shiftKey,
				originalEvent: event
			};
		},

		onKeydown: function(event) {
			var wrappedEvent = this._wrapEvent(event);

			if(this._shouldMoveSceneLeft(wrappedEvent)) { // Ctrl-Left/Ctrl-Down
				this._moveSceneLeft(this._getSceneIdFromElement(event));
			} else if(this._shouldMoveSceneRight(wrappedEvent)) { // Ctrl-Right/Ctrl-Up
				this._moveSceneRight(this._getSceneIdFromElement(event));
			} else if(this._shouldNavigateLeft(wrappedEvent)) { // Left or Down
				this.controller.previousScene();
			} else if(this._shouldNavigateRight(wrappedEvent)) { // Right or Up
				this.controller.nextScene();
			} else if(this._shouldShowContextMenu(wrappedEvent)) { // Shift+F10 - Show toolbar
				this.onSceneOverflowClick(event);
			} else if(this._shouldDeleteScene(wrappedEvent)) { // Delete or Backspace (With no modifier keys)
				this.controller.deleteScene(this._getSceneIdFromElement(event));
			} else if(this._shouldPerformEnter(wrappedEvent)) { // Enter
				$(event.target).click();
			} else if(this._shouldPerformExpand(wrappedEvent)) { //F10 - expand the scene
				this.controller.expandScene();
			} else if (this._shouldPerformTogglePlayPause(wrappedEvent)) { // Spacebar - toggle play/pause
				this.controller.togglePlayPause();
			} else {
				// Don't stop propagation or prevent default.
				return;
			}

			event.stopPropagation();
			event.preventDefault();
		},

		_getSceneIdFromElement: function(event) {
			return event.currentTarget.dataset.modelId;
		},

		_shouldMoveSceneLeft: function(event) {
			return event.modifier && (event.code === 37 || event.code === 38); //Ctrl-Left/Ctrl-Down
		},

		_shouldMoveSceneRight: function(event) {
			return event.modifier && (event.code === 39 || event.code === 40); //Ctrl-Right/Ctrl-Up
		},

		// Left or Down
		_shouldNavigateLeft: function(event) {
			return event.code === 37 || event.code === 38;
		},

		// Right or Up
		_shouldNavigateRight: function(event) {
			return event.code === 39 || event.code === 40;
		},

		// Delete or Backspace (With no modifier keys)
		_shouldDeleteScene: function(event) {
			var sceneId = this._getSceneIdFromElement(event.originalEvent),
				canDelete = (sceneId && this.controller.isAuthoring() && this.controller.getSceneCount() > 1),
				isDeleteOrBackspace = (event.code === 46 || event.code === 8),
				notCtrlOrAlt = !event.modifier && !event.altKey;
			return isDeleteOrBackspace && notCtrlOrAlt && canDelete;
		},

		// Shift-F10 when scene on scene
		_shouldShowContextMenu: function(event) {
			return (event.shiftKey && event.code === 121);
		},

		_shouldPerformEnter: function(event) {
			return event.code === 13 && !$(event.originalEvent.target).hasClass('coachMark');
		},

		// F10 in windows when scene on scene
		_shouldPerformExpand: function(event) {
			return (event.code === 121);
		},

		_shouldPerformTogglePlayPause: function(event) {
			return (event.code === 32);
		},

		_moveSceneLeft: function(sceneId) {
			if(this.controller.isAuthoring() && sceneId) {
				this.controller.moveSceneLeft(sceneId);
			}
		},

		_moveSceneRight: function(sceneId) {
			if(this.controller.isAuthoring() && sceneId) {
				this.controller.moveSceneRight(sceneId);
			}
		},

		_getSceneScrollPosition: function($scene) {
			const scrollLeft = this.$el.scrollLeft();
			const position = $scene.position();
			const marginLeft = parseInt($scene.css('margin-left'), 10);
			const width = this.$el.outerWidth();

			var left = position.left,
				right = position.left + $scene.outerWidth(true);
			if(left < 0) {
				left += scrollLeft - marginLeft - 30;
			} else if(right > width) {
				left = scrollLeft + marginLeft + 30 + (right - width);
			} else {
				left = scrollLeft;
			}

			if($scene.index() === this.$sceneList.children().length - 1) {
				left += $scene.width() * 2;
			}

			return {
				position: left,
				scrollLeft: scrollLeft
			};
		}
	});

	return FilmStripView;
});
